import { useEffect, useState } from "react";
import { fetchBreakdown, send, type BreakdownData } from "../api.js";

/**
 * The words this shop uses for work.
 *
 * Both lists were TypeScript literals — adding a work type meant a deploy, and
 * "which states count as work in progress" was a constant in the same file as
 * the scheduling maths. They are the scheduler's vocabulary, and the second one
 * is load-bearing: a step in a WIP state is frozen, so no solve and no PM can
 * move it. The screen has to say that where the checkbox is, not in a manual.
 */
export function Work() {
  const [data, setData] = useState<BreakdownData | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [newType, setNewType] = useState("");
  const [newState, setNewState] = useState("");

  const load = async () => {
    try { setData(await fetchBreakdown()); setErr(null); }
    catch (e) { setErr((e as Error).message); }
  };
  useEffect(() => { void load(); }, []);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true); setErr(null);
    try { await fn(); await load(); }
    catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };

  if (err && !data) return <div className="err">{err}</div>;
  if (!data) return <p className="muted">Loading…</p>;

  const readOnly = data.user.role !== "admin";
  const countType = (code: string) => data.phases.filter((p) => p.workType === code).length;
  const countState = (id: string) => data.phases.filter((p) => p.workflowState === id).length;
  const wipCount = data.workflowStates.filter((s) => s.countsAsWip).length;

  return (
    <div>
      <p className="hint" style={{ marginTop: 0 }}>
        Both lists below used to be fixed in the code. Adding a work type or a state is a
        change you make here.
      </p>
      {err && <div className="err" style={{ marginBottom: 12 }}>{err}</div>}

      <div className="run-sec">
        <h3>
          Work types
          <span className="run-sec-note">what kind of work a step is</span>
        </h3>
        <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
          A step's work type is what the Backlog groups by, and what a rule means when it is
          scoped to “all Detail work”. Retiring one keeps existing steps readable but takes it
          off the menus.
        </p>
        <div className="scroll">
          <table className="qt" style={{ minWidth: 700 }}>
            <thead>
              <tr>
                <th>Code</th><th>Shown as</th><th>Note</th>
                <th className="r">Steps</th><th className="r">Order</th><th>In use</th><th></th>
              </tr>
            </thead>
            <tbody>
              {data.workTypes.map((w) => (
                <tr key={w.code} style={{ opacity: busy ? 0.6 : w.active ? 1 : 0.55 }}>
                  <td className="mono"><b>{w.code}</b></td>
                  <td>
                    <input defaultValue={w.label} disabled={readOnly}
                      onBlur={(e) => e.target.value !== w.label && void act(() =>
                        send(`/work-types/${encodeURIComponent(w.code)}`, "PATCH", { label: e.target.value }))} />
                  </td>
                  <td style={{ minWidth: 260 }}>
                    <input defaultValue={w.note ?? ""} disabled={readOnly} style={{ width: "100%" }}
                      onBlur={(e) => e.target.value !== (w.note ?? "") && void act(() =>
                        send(`/work-types/${encodeURIComponent(w.code)}`, "PATCH", { note: e.target.value }))} />
                  </td>
                  <td className="r mono">{countType(w.code)}</td>
                  <td className="r">
                    <input className="num" type="number" step="10" defaultValue={w.sortOrder} disabled={readOnly}
                      onBlur={(e) => Number(e.target.value) !== w.sortOrder && void act(() =>
                        send(`/work-types/${encodeURIComponent(w.code)}`, "PATCH", { sortOrder: Number(e.target.value) }))} />
                  </td>
                  <td>
                    <input type="checkbox" checked={w.active} disabled={readOnly}
                      onChange={(e) => void act(() =>
                        send(`/work-types/${encodeURIComponent(w.code)}`, "PATCH", { active: e.target.checked }))} />
                  </td>
                  <td>
                    {!readOnly && (
                      <button className="btn" disabled={busy}
                        onClick={() => void act(async () => {
                          const n = countType(w.code);
                          const msg = n
                            ? `${n} step${n === 1 ? "" : "s"} use “${w.code}”. It will be retired rather than deleted, so those stay readable. Continue?`
                            : `Delete the work type “${w.code}”?`;
                          if (!confirm(msg)) return;
                          await send(`/work-types/${encodeURIComponent(w.code)}`, "DELETE");
                        })}>
                        {countType(w.code) ? "Retire" : "Delete"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!readOnly && (
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <input placeholder="e.g. Site survey" value={newType}
              onChange={(e) => setNewType(e.target.value)} />
            <button className="btn on" disabled={busy || !newType.trim()}
              onClick={() => void act(async () => {
                await send("/work-types", "POST", { code: newType.trim() });
                setNewType("");
              })}>
              Add a work type
            </button>
          </div>
        )}
      </div>

      <div className="run-sec">
        <h3>
          Workflow states
          <span className="run-sec-note">where a step is in its life</span>
        </h3>
        <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
          <b>Work in progress</b> is the one that does something: a step in one of those states
          is frozen — the optimizer may not move it and a PM may not reorder it, because it is
          already being worked on. <b>Finished</b> marks the end of the line.
        </p>
        <div className="scroll">
          <table className="qt" style={{ minWidth: 640 }}>
            <thead>
              <tr>
                <th>State</th><th>Shown as</th><th className="r">Steps</th>
                <th className="r">Order</th><th>Work in progress</th><th>Finished</th><th></th>
              </tr>
            </thead>
            <tbody>
              {data.workflowStates.map((s) => (
                <tr key={s.stateId} style={{ opacity: busy ? 0.6 : 1 }}>
                  <td className="mono"><b>{s.stateId}</b></td>
                  <td>
                    <input defaultValue={s.label} disabled={readOnly}
                      onBlur={(e) => e.target.value !== s.label && void act(() =>
                        send(`/workflow-states/${encodeURIComponent(s.stateId)}`, "PATCH", { label: e.target.value }))} />
                  </td>
                  <td className="r mono">{countState(s.stateId)}</td>
                  <td className="r">
                    <input className="num" type="number" step="10" defaultValue={s.sortOrder} disabled={readOnly}
                      onBlur={(e) => Number(e.target.value) !== s.sortOrder && void act(() =>
                        send(`/workflow-states/${encodeURIComponent(s.stateId)}`, "PATCH", { sortOrder: Number(e.target.value) }))} />
                  </td>
                  <td>
                    <input type="checkbox" checked={s.countsAsWip} disabled={readOnly}
                      title={s.countsAsWip && wipCount === 1
                        ? "the only state counting as work in progress"
                        : "a step in this state cannot be moved by a solve"}
                      onChange={(e) => void act(() =>
                        send(`/workflow-states/${encodeURIComponent(s.stateId)}`, "PATCH", { countsAsWip: e.target.checked }))} />
                  </td>
                  <td>
                    <input type="checkbox" checked={s.isTerminal} disabled={readOnly}
                      onChange={(e) => void act(() =>
                        send(`/workflow-states/${encodeURIComponent(s.stateId)}`, "PATCH", { isTerminal: e.target.checked }))} />
                  </td>
                  <td>
                    {!readOnly && (
                      <button className="btn" disabled={busy || countState(s.stateId) > 0}
                        title={countState(s.stateId) ? "steps are in this state" : ""}
                        onClick={() => void act(async () => {
                          if (!confirm(`Delete the state “${s.stateId}”?`)) return;
                          await send(`/workflow-states/${encodeURIComponent(s.stateId)}`, "DELETE");
                        })}>
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!readOnly && (
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <input placeholder="e.g. Awaiting parts" value={newState}
              onChange={(e) => setNewState(e.target.value)} />
            <button className="btn on" disabled={busy || !newState.trim()}
              onClick={() => void act(async () => {
                await send("/workflow-states", "POST", { stateId: newState.trim() });
                setNewState("");
              })}>
              Add a state
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
